// packages/web-ui/src/panels/chat-panel/markdown-stream-state.ts
//
// W2-S5 AC #5 — Stream-safe markdown buffer.
//
// Ported VERBATIM (algorithmically) from
// `packages/webview-ui/src/panels/chat-panel/utils/markdown-stream-state.ts`
// (Epic 24 hardened against 19 known fragmentation bugs). DO NOT re-derive —
// preserves identical safe-boundary semantics so streamed markdown never
// flickers across SSE chunks.
//
// Pure utility class — zero framework dependencies.

/**
 * MarkdownStreamState — Safe-boundary buffer for streaming markdown.
 *
 * Accumulates streaming tokens and yields renderable chunks only at
 * markdown-safe boundaries (closed code fences, blank lines / paragraph
 * boundaries). Never splits inside an open code fence.
 */
export class MarkdownStreamState {
  /** Accumulated text waiting for a safe boundary. */
  private pending = '';

  /** Tracks open / close code-fence pairs (outermost = depth 1). */
  private fenceDepth = 0;

  /**
   * Append an incremental token and return the safe-to-render portion,
   * or `null` when no safe boundary has been reached yet.
   */
  push(delta: string): string | null {
    this.pending += delta;
    const boundary = this.findSafeBoundary();
    if (boundary === null) {
      return null;
    }
    const safe = this.pending.slice(0, boundary);

    // Never return empty / whitespace-only strings — use null instead.
    // Check BEFORE slicing pending so whitespace is not silently dropped.
    if (safe.trim().length === 0) {
      return null;
    }

    this.pending = this.pending.slice(boundary);

    // Recalculate fenceDepth for the remaining pending content.
    this.fenceDepth = this.computeFenceDepth(this.pending);

    return safe;
  }

  /**
   * Flush all remaining buffered content regardless of safe boundaries.
   * Resets internal state (`pending` cleared, `fenceDepth` zeroed).
   * Returns `null` when the buffer is empty / whitespace-only.
   */
  flush(): string | null {
    const content = this.pending;
    this.pending = '';
    this.fenceDepth = 0;
    if (content.trim().length === 0) {
      return null;
    }
    return content;
  }

  /** Diagnostic helper — current pending length. */
  get pendingLength(): number {
    return this.pending.length;
  }

  // -------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------

  /**
   * Scan `pending` for the latest safe split point.
   *
   * Always walks from the start of `pending` with fenceDepth = 0, since
   * `pending` contains the full un-emitted buffer (previously emitted
   * content has already been sliced off).
   *
   * Priority: maximise the renderable chunk by returning the latest
   * safe boundary found. Closed code fences and blank lines (outside
   * fences) are both valid boundaries.
   *
   * Returns the character index **after** the safe boundary, or `null`.
   */
  private findSafeBoundary(): number | null {
    const lines = this.pending.split('\n');

    let bestBoundary: number | null = null;
    let charIndex = 0;
    // Always start depth at 0 — `pending` is the full un-emitted buffer.
    let depth = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? '';
      const isLastLine = i === lines.length - 1;

      // charIndex after this line (including '\n' except for last segment).
      charIndex += line.length + (isLastLine ? 0 : 1);

      // Check if this line is a code fence.
      if (this.isFenceLine(line)) {
        if (depth === 0) {
          // Opening fence (may have language specifier).
          depth++;
        } else if (this.isClosingFence(line)) {
          depth--;
          if (depth === 0) {
            bestBoundary = charIndex;
          }
        } else {
          // Nested opening fence (has language specifier).
          depth++;
        }
        continue;
      }

      // Blank-line detection — only outside code fences.
      // A blank line means current line is whitespace-only, AND it is
      // preceded by a newline (i > 0). We also require it not to be
      // the very last line (we need content *after* the boundary).
      if (depth === 0 && i > 0 && line.trim().length === 0 && !isLastLine) {
        bestBoundary = charIndex;
      }
    }

    // Update fenceDepth to reflect the full walk (used only when no
    // boundary is found; when a boundary IS found, push() recalculates).
    this.fenceDepth = depth;

    return bestBoundary;
  }

  /**
   * Compute fence depth for a given text fragment by walking its lines.
   */
  private computeFenceDepth(text: string): number {
    if (text.length === 0) return 0;
    const lines = text.split('\n');
    let depth = 0;
    for (const line of lines) {
      if (this.isFenceLine(line)) {
        if (depth === 0) {
          depth++;
        } else if (this.isClosingFence(line)) {
          depth--;
        } else {
          depth++;
        }
      }
    }
    return depth;
  }

  /**
   * Determine whether `line` is a code fence (opening or closing).
   *
   * Matches lines like:
   *   ```           (closing or opening with no lang)
   *   ```typescript (opening with lang)
   *   ````          (4+ backticks)
   */
  private isFenceLine(line: string): boolean {
    return /^\s*`{3,}\s*(\w+)?\s*$/.test(line);
  }

  /**
   * Returns true when `line` matches the closing fence pattern — triple
   * backticks with no language specifier.
   */
  private isClosingFence(line: string): boolean {
    return /^\s*`{3,}\s*$/.test(line);
  }
}

/**
 * Strip fenced ```sysml ... ``` blocks per FR-CHAT-12 (raw .sysml never
 * surfaces in chat). Replaces each block with a single italic note so the
 * user sees the suppression but the raw SysML never reaches the DOM.
 *
 * Used by `<MessageBubble>` BEFORE handing the content to `marked`. Pure —
 * no DOM access.
 */
export function stripSysmlFences(content: string): string {
  // Match fenced blocks whose info string is exactly `sysml` (case-insensitive).
  // The regex is non-greedy across the body and tolerates trailing whitespace
  // on the opening fence line.
  const SYSML_FENCE_RE = /^[ \t]*```[ \t]*sysml[ \t]*\r?\n[\s\S]*?\r?\n[ \t]*```[ \t]*$/gim;
  return content.replace(
    SYSML_FENCE_RE,
    '_SysML content suppressed — open the changeset to view._',
  );
}
